'use client';

import React, { useCallback, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  Star,
  X,
  ChevronRight,
  Briefcase,
  DollarSign,
  Users,
  Truck,
  ClipboardList,
  BarChart3,
  Headphones,
  Calendar,
  Settings,
  ScanFace,
  type LucideIcon,
} from 'lucide-react';
import { useFavourites, type FavouriteItem } from '@/hooks/useFavourites';
import {
  canNavigateOperationsFavouriteInPlace,
  favouriteHrefMatchesLocation,
} from '@/lib/navigation/operationsFavouritesDeepLink';

const MODULE_ICON_MAP: Record<string, LucideIcon> = {
  'total-operations': Briefcase,
  'total-finance': DollarSign,
  'total-workforce': Users,
  'total-attendance': ScanFace,
  'total-connect': Headphones,
  'total-support': ClipboardList,
  'total-analytics': BarChart3,
  'total-fleet': Truck,
  'total-calendar': Calendar,
  'total-settings': Settings,
};

function resolveIcon(item: FavouriteItem): LucideIcon {
  if (item.icon && MODULE_ICON_MAP[item.icon]) {
    return MODULE_ICON_MAP[item.icon];
  }
  if (item.moduleKey && MODULE_ICON_MAP[item.moduleKey]) {
    return MODULE_ICON_MAP[item.moduleKey];
  }
  const hrefModuleKey = item.href.match(/modules\/([\w-]+)/)?.[1];
  if (hrefModuleKey && MODULE_ICON_MAP[hrefModuleKey]) {
    return MODULE_ICON_MAP[hrefModuleKey];
  }
  return Star;
}

function isActiveFavourite(
  pathname: string | null,
  search: string,
  item: FavouriteItem,
): boolean {
  if (!pathname) return false;
  const href = item.href.trim();
  if (href.includes('?')) {
    return favouriteHrefMatchesLocation(href, pathname, search);
  }
  const normalizedPath = pathname.replace(/\/+$/, '');
  const pathOnly = href.split('?')[0]?.replace(/\/+$/, '') ?? '';
  return normalizedPath === pathOnly || normalizedPath.startsWith(`${pathOnly}/`);
}

interface FavouritesBarProps {
  className?: string;
}

export default function FavouritesBar({ className }: FavouritesBarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ? `?${searchParams.toString()}` : '';
  const { favourites, removeFavourite, barVisible, count } = useFavourites();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleActivate = useCallback((event: React.MouseEvent<HTMLAnchorElement>, item: FavouriteItem) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }

    if (!canNavigateOperationsFavouriteInPlace(pathname, item.href)) {
      return;
    }

    event.preventDefault();
    router.replace(item.href, { scroll: false });
  }, [pathname, router]);

  const handleRemove = useCallback((event: React.MouseEvent, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    removeFavourite(id);
  }, [removeFavourite]);

  if (!barVisible) return null;

  return (
    <div
      className={`w-full border-b theme-border bg-gradient-to-r from-slate-50 via-white to-slate-50 dark:from-slate-900/95 dark:via-slate-900 dark:to-slate-900/95 shadow-[0_1px_3px_rgba(0,0,0,0.05)] backdrop-blur-sm ${className || ''}`}
    >
      <div className="w-full px-2 lg:px-4 xl:px-6 2xl:px-8">
        <div className="flex items-center gap-1 py-1 overflow-x-auto scrollbar-none">
          <div className="flex items-center gap-1 pr-1.5 mr-1 border-r theme-border flex-shrink-0">
            <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] theme-text-secondary whitespace-nowrap">
              Favourites
            </span>
          </div>

          {count === 0 && (
            <span className="text-[11px] theme-text-secondary px-1">
              Use the star on the Service Operations header to save the current Jobs/Appointments view (e.g. Jobs › List). Toggle the star in the top bar to show or hide this row.
            </span>
          )}

          {favourites.map((item) => {
            const Icon = resolveIcon(item);
            const isActive = isActiveFavourite(pathname, search, item);
            const isHovered = hoveredId === item.id;

            return (
              <div
                key={item.id}
                className="relative flex-shrink-0 group"
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <Link
                  href={item.href}
                  onClick={(event) => handleActivate(event, item)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12px] font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800'
                      : 'theme-text-secondary hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-slate-800 dark:hover:text-gray-100'
                  }`}
                  title={`${item.label}${item.section ? ` — ${item.section}` : ''}`}
                >
                  <Icon className={`h-3 w-3 flex-shrink-0 ${isActive ? 'text-blue-500' : ''}`} />
                  <span className="max-w-[140px] truncate">{item.label}</span>
                  {item.section && (
                    <>
                      <ChevronRight className="h-2.5 w-2.5 opacity-40 flex-shrink-0" />
                      <span className="max-w-[100px] truncate opacity-70 text-[11px]">{item.section}</span>
                    </>
                  )}
                </Link>

                {isHovered && (
                  <button
                    onClick={(e) => handleRemove(e, item.id)}
                    className="absolute -top-1 -right-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white shadow-sm transition-transform hover:scale-110"
                    title="Remove from favourites"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
